/**
 * Monaco Editor Language Configuration for MethodScript
 *
 * This module provides language configuration, syntax highlighting (Monarch tokenizer),
 * and theme definitions for MethodScript in Monaco Editor.
 *
 * Use this in all Monaco-based editors (WASM, Blazor, VSCode) to ensure consistent
 * syntax highlighting and language behavior.
 */

import { measurementCommands } from './generated/syntax-data.js';
import { monarchTokensProvider } from './monarch-generated.js';

/**
 * Language configuration for MethodScript
 * Defines comments, brackets, auto-closing pairs, etc.
 */
export const languageConfig = {
    comments: {
        lineComment: '#',
    },
    // Keyword pairs below are declared as brackets so Monaco can do bracket matching and folding on
    // them. They are intentionally NOT colorized: an empty colorizedBracketPairs disables rainbow
    // bracket-pair colouring for this language, so keywords keep their theme token colour instead of
    // being painted with nesting-level bracket colours (and literal brackets stay unsyntaxed).
    colorizedBracketPairs: [] as Array<[string, string]>,
    brackets: [
        ['[', ']'],
        ['if', 'endif'],
        ['loop', 'endloop'],
        ['pck_start', 'pck_end'],
        // Every measurement loop is terminated with endloop, so the pairs come from the generated
        // command list rather than being restated here.
        ...measurementCommands.map((command): [string, string] => [command, 'endloop']),
    ] as Array<[string, string]>,
    autoClosingPairs: [
        { open: '[', close: ']' },
        { open: '"', close: '"' },
        { open: '(', close: ')' },
    ],
    surroundingPairs: [
        { open: '[', close: ']' },
        { open: '"', close: '"' },
        { open: '(', close: ')' },
    ],
    folding: {
        markers: {
            start: new RegExp('^\\s*#region\\b'),
            end: new RegExp('^\\s*#endregion\\b'),
        },
    },
};

/**
 * Monarch Tokenizer for MethodScript syntax highlighting
 * Generated from syntax-definitions.ts - DO NOT EDIT MANUALLY
 * Run `npm run generate-syntax` to regenerate
 */
export { monarchTokensProvider } from './monarch-generated.js';

/**
 * Dark theme definition for MethodScript.
 * Built on Monaco's built-in `vs-dark` base theme, so tokens without an explicit rule
 * inherit sensible dark defaults.
 */
export const methodscriptTheme = {
    base: 'vs-dark' as const,
    inherit: true,
    rules: [
        { token: 'keyword', foreground: 'FFD700', fontStyle: 'bold' }, // Gold for control flow
        { token: 'keyword.measurement', foreground: 'FFD700', fontStyle: 'bold' }, // Gold for measurement loops
        { token: 'keyword.command', foreground: 'DCDCAA' }, // Light yellow for commands
        { token: 'keyword.label', foreground: 'FFD700', fontStyle: 'bold' }, // Gold for labels (on_finished:)
        { token: 'type.identifier', foreground: '4EC9B0' }, // Teal for VarTypes
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'number.integer', foreground: 'B5CEA8' },
        { token: 'number.float', foreground: 'B5CEA8' },
        { token: 'number.si', foreground: 'B5CEA8' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'string.escape', foreground: 'D7BA7D' },
        { token: 'string.interpolation.delimiter', foreground: 'FFD700' }, // Gold for { } in f-strings
        { token: 'identifier.interpolation', foreground: '9CDCFE' }, // Blue for variables in interpolation
        { token: 'delimiter.bracket', foreground: 'FFD700' }, // Gold for [ ] in interpolation
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
        { token: 'operator', foreground: 'D4D4D4' },
        { token: 'identifier', foreground: '9CDCFE' },
    ],
    colors: {
        'editor.background': '#1E1E1E',
        'editor.foreground': '#D4D4D4',
        'editorLineNumber.foreground': '#858585',
        'editorCursor.foreground': '#AEAFAD',
    },
};

/**
 * Light theme definition for MethodScript.
 * Built on Monaco's built-in `vs` (light) base theme so the editor matches a light host such as
 * PSTraceExpress. Token foregrounds mirror the dark theme's semantics using the VS Code Light+
 * palette, keeping syntax highlighting recognizable across both themes.
 */
export const methodscriptLightTheme = {
    base: 'vs' as const,
    inherit: true,
    rules: [
        { token: 'keyword', foreground: '0090C9', fontStyle: 'bold' }, // Blue for control flow
        { token: 'keyword.measurement', foreground: '0090C9', fontStyle: 'bold' }, // Blue for measurement loops
        { token: 'keyword.command', foreground: '0090C9' }, // Blue for commands
        { token: 'keyword.label', foreground: 'C42B1C', fontStyle: 'bold' }, // Red for labels (on_finished:)
        { token: 'type.identifier', foreground: 'D97000' }, // Orange for VarTypes
        { token: 'number', foreground: '45587E' }, // Slate for values
        { token: 'number.integer', foreground: '45587E' },
        { token: 'number.float', foreground: '45587E' },
        { token: 'number.si', foreground: '45587E' },
        { token: 'string', foreground: '45587E' }, // Slate for string values
        { token: 'string.escape', foreground: '45587E' },
        { token: 'string.interpolation.delimiter', foreground: '000000' }, // Unsyntaxed punctuation for { } in f-strings
        { token: 'identifier.interpolation', foreground: '32B34A' }, // Green for variables in interpolation
        { token: 'delimiter.bracket', foreground: '000000' }, // Unsyntaxed punctuation for [ ] in interpolation
        { token: 'comment', foreground: '868686', fontStyle: 'italic' },
        { token: 'operator', foreground: '000000' },
        { token: 'identifier', foreground: '32B34A' }, // Green for variable names
    ],
    colors: {
        'editor.background': '#FFFFFF',
        'editor.foreground': '#000000',
        'editorLineNumber.foreground': '#237893',
        'editorCursor.foreground': '#000000',
        'editorError.foreground': '#C42B1C',
    },
};

/**
 * Combined configuration object for easy import
 */
export const methodscriptConfig = {
    languageConfig,
    monarchTokensProvider,
    theme: methodscriptTheme,
    lightTheme: methodscriptLightTheme,
};
